import moment from 'moment';
import { useLocation } from 'react-router-dom';

function getMarketNames(marketIdList, allMarkets) {
	if (!Array.isArray(allMarkets)) {
		throw new Error('allMarkets must be an array');
	}

	const idToNameMap = allMarkets.reduce((map, item) => {
		map[item.id] = item.name;
		return map;
	}, {});

	const marketIds = marketIdList ? marketIdList.split(',') : [];
	//const marketNames = marketIds.map((id) => idToNameMap[id] || 'Unknown');
	const marketNames = marketIds
		.filter((id) => idToNameMap[id]) // Filter out unmatched IDs
		.map((id) => idToNameMap[id]); // Map matched IDs to their names

	return marketNames;
}

export function getBasedOnBrandData(queryString, allMarkets, fabricList) {
	const params = new URLSearchParams(queryString);
	const brandCodeList = params.get('brandCodeList');
	const marketIdList = params.get('marketIdList');
	const fabricTypeList = params.get('fabricTypeList');
	const categoryIdList = params.get('categoryIdList');
	const startDate = params.get('startDate');
	const endDate = params.get('endDate');

	// Process brandCodeList
	const brandCount = brandCodeList ? brandCodeList.split(',').length : 0;

	// Process date range
	const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : '';

	// Process marketIdList (not needed for the final object, but extracted)
	const market = allMarkets && allMarkets.length > 0 ? getMarketNames(marketIdList, allMarkets) : [];

	// Process fabricTypeList
	const fabricType =
		fabricList?.length > 0 && fabricTypeList?.length > 0
			? fabricList
					.filter((item) => fabricTypeList.includes(item.constant)) // Filter by constants
					.map((item) => item.name) // Map to get the `name` property
					.join(', ')
			: ''; // Join names with a comma

	// Process categoryIdList
	const categoryCount = categoryIdList ? categoryIdList.split(',').length : 0;

	// Construct the sourceData object
	const sourceData = {
		brandCount,
		date: dateRange,
		market,
		fabricType,
		category: categoryCount
	};

	return sourceData;
}

export function _getBasedOnBrandData(queryString, allMarkets, fabricList, brands) {
	const params = new URLSearchParams(queryString);
	const location = useLocation();
	const brandCodeList = params.get('brandCodeList');
	const marketIdList = params.get('marketIdList');
	const fabricTypeList = params.get('fabricTypeList');
	const categoryIdList = params.get('categoryIdList');
	const startDate = params.get('startDate');
	const endDate = params.get('endDate');
	const instagramUserNameList = params.get('instagramUserNameList');
	const { source } = location?.state || {};

	// Process brandCodeList
	const brandNames =
		brands &&
		brandCodeList &&
		brands.length > 0 &&
		brands.filter((brand) => brandCodeList.includes(brand.code)).map((brand) => brand.name);

	const brandCount = brandCodeList ? brandCodeList.split(',').length : 0;

	const influencerCount = instagramUserNameList ? instagramUserNameList.split(',').length : 0;
	const influencerNames = instagramUserNameList ? instagramUserNameList.split(',') : [];

	// Process date range
	// DD MMM, YYYY
	// moment(new Date()).format('DD MMM [AT] hh:mm A')
	const dateRange =
		startDate && endDate
			? `${moment(startDate).format('DD MMM, YY')} - ${moment(endDate).format('DD MMM, YY')}`
			: '';

	// Process marketIdList (not needed for the final object, but extracted)
	const market = allMarkets && allMarkets.length > 0 ? getMarketNames(marketIdList, allMarkets) : [];

	// Process fabricTypeList
	const fabricType =
		fabricList?.length > 0 && fabricTypeList?.length > 0
			? fabricList
					.filter((item) => fabricTypeList.includes(item.constant)) // Filter by constants
					.map((item) => item.name) // Map to get the `name` property
			: []; // Join names with a comma

	const categoryCount = categoryIdList ? categoryIdList.split(',').length : 0;

	let cardData = [];
	if (fabricList?.length > 0) {
		if (source === 'Instagram') {
			cardData = [
				{ title: 'Influencer', values: influencerCount, items: influencerNames, isPopup: true }, // Array of values
				{ title: 'Date', values: dateRange }, // String
				{ title: 'Market', values: market }, // Number
				{ title: 'Fabric Type', values: fabricType }, // Array of values
				{ title: 'Category', values: categoryCount } // String
			];
		} else {
			cardData = [
				{ title: 'Brand', values: brandCount, items: brandNames, isPopup: true }, // Array of values
				{ title: 'Date', values: dateRange }, // String
				{ title: 'Market', values: market }, // Number
				{ title: 'Fabric Type', values: fabricType }, // Array of values
				{ title: 'Category', values: categoryCount } // String
			];
		}
	} else {
		if (source === 'Instagram') {
			cardData = [
				{ title: 'Influencer', values: influencerCount, items: influencerNames, isPopup: true }, // Array of values
				{ title: 'Date', values: dateRange }, // String
				{ title: 'Market', values: market }, // Number
				{ title: 'Category', values: categoryCount } // String
			];
		} else {
			cardData = [
				{ title: 'Brand', values: brandCount, items: brandNames, isPopup: true }, // Array of values
				{ title: 'Date', values: dateRange }, // String
				{ title: 'Market', values: market }, // Number
				{ title: 'Category', values: categoryCount } // String
			];
		}
	}

	return cardData;
}

export const brandHeaders = {
	brandCount: 'Based on Brands',
	date: 'Date',
	market: 'Market',
	fabricType: 'Fabric Type',
	category: 'Category'
};

export function getDetailsCount(details) {
	let price = null;
	if (details?.minPrice) {
		price = { ...price, min: details?.minPrice };
	}
	if (details?.midPrice) {
		price = { ...price, mid: details?.midPrice?.toFixed(0) };
	}
	if (details?.maxPrice) {
		price = { ...price, max: details?.maxPrice };
	}
	const sourceData = {
		styles: details?.noOfProducts?.toLocaleString(),
		price
	};
	return sourceData;
}

export const detailsCountHeaders = {
	styles: 'Styles',
	price: 'Price'
};

export const formatFabricName = (fabricName) => {
	if (!fabricName) return '';
	return fabricName?.split('+');
};

export const areArraysEqual = (array1, array2) => {
	if (!array2) return false;
	if (array1.length !== array2.length) {
		return false;
	}
	return array1.every((item, index) => item === array2[index]);
};
export const getValidateHexCode = (input) => {
	// Remove any surrounding quotes from the input
	const cleanedInput = input.replace(/["']/g, '');

	// Regular expression to check for valid hex color code
	const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

	// Check if cleaned input is a valid hex code
	if (hexRegex.test(cleanedInput)) {
		return cleanedInput;
	} else {
		return 'Invalid hex code';
	}
};
export const colorCategory = [
	{ name: 'Solid Color', count: 0 },
	{ name: 'Graphics', count: 0 },
	{ name: 'AOP', count: 0 }
];
export const filterName = {
	'key-fabric': 'fiberList',
	'key-color': 'colorList',
	'key-graphics': 'graphicsContentList'
};
export const tabMapping = {
	'key-color': 0,
	'key-graphics': 1,
	'key-prints': 2
};
export const reverseTabMapping = {
	0: 'key-color',
	1: 'key-graphics',
	2: 'key-prints'
};

export const sourceType = {
	Instagram: 'INSTAGRAM',
	Brand: 'BRAND'
};
export const graphicsType = {
	'key-graphics': 'GRAPHICS',
	'key-prints': 'AOP'
};
export const getQueryParamForDetails = (product) => {
	if (!product) return;
	let param = '';
	if (product?.marketId) {
		param += `marketIdList=${product?.marketId}&`;
	}
	if (product?.categoryId) {
		param += `categoryIdList=${product?.categoryId}&`;
	}
	if (product?.color) {
		param += `colorList=${product?.color}&`;
	}
	if (product?.graphicsType === 'Print and Embroidery') {
		param += `graphicsType=GRAPHICS&`;
	}
	if (product?.graphicsType === 'AOP and YD') {
		param += `graphicsType=AOP&`;
	}
	if (product?.graphicsType === 'Solid') {
		param += `graphicsType=SOLID&`;
	}

	if (param !== '') {
		param = `?${param}`;
	}

	return param;
};
export const cleanUrl = (url) => {
	return url.replace(/^['"]|['"]$/g, '').trim();
};

export function getTitle(tab) {
	if (tab === 'key-fabric') return 'Key Fabric';
	else if (tab === 'key-graphics') return 'Key Graphics';
	else if (tab === 'key-color') return 'Key Color';
	else if (tab === 'key-theme') return 'Key Theme';
	else if (tab === 'key-prints') return 'Key AOP';
	return '';
}

export const selectedName = {
	'key-color': 'colorName',
	'key-graphics': 'graphicsName',
	'key-prints': 'graphicsName',
	'key-theme': 'themeName'
};

export function capitalizeFirstLetter(str) {
	if (!str) return '';

	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getMultiplyWith(length) {
	if (length > 90) return 6;
	else if (length >= 80 && length < 90) return 5;
	else if (length >= 70 && length < 80) return 4.5;
	else if (length >= 60 && length < 70) return 4;
	else if (length >= 50 && length < 60) return 3.5;
	else if (length > 40 && length < 50) return 3;
	else return 1;
}
